import { makeFetchTransport } from '@sentry/react';
import { filterSurveyMetric } from '../flow/presentation/surveyObservability';

type Transport = ReturnType<typeof makeFetchTransport>;
type Envelope = Parameters<Transport['send']>[0];
type EnvelopeItem = Envelope[1][number];

/** The SDK adds scope attributes AFTER beforeSendMetric, so enforce the allowlist at transport too. */
export function filterSurveyMetricEnvelope(envelope: Envelope): Envelope {
  const items = (envelope[1] as EnvelopeItem[]).flatMap((item): EnvelopeItem[] => {
    if (item[0].type !== 'trace_metric') return [item];
    // Narrow the discriminated tuple explicitly (the header is the discriminator).
    const metricItem = item as Extract<Envelope[1][number], [{ type: 'trace_metric' }, unknown]>;
    const payload = metricItem[1];
    const metrics = payload.items.flatMap((metric) => {
      const raw = Object.fromEntries(Object.entries(metric.attributes ?? {}).map(([key, attribute]) => [key, attribute.value]));
      const filtered = filterSurveyMetric({ name: metric.name, attributes: raw });
      if (!filtered) return [];
      const attributes = Object.fromEntries(Object.keys(filtered.attributes).map((key) => [key, metric.attributes![key]]));
      const sequence = metric.attributes?.['sentry.timestamp.sequence'];
      if (sequence?.type === 'integer' && Number.isSafeInteger(sequence.value) && sequence.value >= 0)
        attributes['sentry.timestamp.sequence'] = sequence;
      return [{ ...metric, attributes }];
    });
    if (!metrics.length) return [];
    return [[{ ...metricItem[0], item_count: metrics.length }, {
      ...payload, ingest_settings: { infer_ip: 'never', infer_user_agent: 'never' }, items: metrics,
    }]];
  });
  return [envelope[0], items] as Envelope;
}

export const makeSurveyTelemetryTransport: typeof makeFetchTransport = (options) => {
  const transport = makeFetchTransport(options);
  return {
    flush: (timeout) => transport.flush(timeout),
    send: (envelope) => {
      const filtered = filterSurveyMetricEnvelope(envelope);
      return filtered[1].length ? transport.send(filtered) : Promise.resolve({});
    },
  };
};
